<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');
const currentPage = ref(1);
let searchTimer;

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const agentsMeta = computed(() => getters['agents/getMeta'].value);

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const apiBaseUrl = computed(
  () =>
    `${window.location.origin}/api/v1/accounts/${currentAccountId.value}/agents`
);
const customRoles = useMapGetter('customRole/getCustomRoles');

const fetchAgents = async () => {
  try {
    await store.dispatch('agents/get', {
      page: currentPage.value,
      q: searchQuery.value.trim(),
    });
  } catch (error) {
    // A 401 is expected while the global auth flow clears an expired or
    // unsuccessful OAuth session. Do not surface it as an unhandled error.
    if (error?.response?.status !== 401) {
      useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
    }
  }
};

const changePage = page => {
  currentPage.value = page;
  fetchAgents();
};

watch(searchQuery, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchAgents();
  }, 250);
});

onMounted(() => {
  fetchAgents();
  store.dispatch('customRole/getCustomRole');
});

onBeforeUnmount(() => clearTimeout(searchTimer));

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    // Roles are supplied by the API and map to the fixed AGENT_TYPES catalog.
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
  fetchAgents();
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
  fetchAgents();
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    if (agentList.value.length === 1 && currentPage.value > 1) {
      currentPage.value -= 1;
    }
    await fetchAgents();
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col lg:flex-row gap-8 w-full">
    <div class="flex-1">
      <SettingsLayout
        :is-loading="uiFlags.isFetching"
        :loading-message="$t('AGENT_MGMT.LOADING')"
        :no-records-found="!agentList.length"
        :no-records-message="$t('AGENT_MGMT.LIST.404')"
      >
        <template #header>
          <BaseSettingsHeader
            v-model:search-query="searchQuery"
            :title="$t('AGENT_MGMT.HEADER')"
            :description="$t('AGENT_MGMT.DESCRIPTION')"
            :link-text="$t('AGENT_MGMT.LEARN_MORE')"
            :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
            feature-name="agents"
          >
            <template v-if="agentsMeta.totalEntries" #count>
              <span class="text-body-main text-n-slate-11">
                {{ $t('AGENT_MGMT.COUNT', { n: agentsMeta.totalEntries }) }}
              </span>
            </template>
            <template #actions>
              <Button
                :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
                size="sm"
                @click="openAddPopup"
              />
            </template>
          </BaseSettingsHeader>
        </template>
        <template #body>
          <span
            v-if="!agentList.length && searchQuery"
            class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.NO_RESULTS') }}
          </span>
          <div v-else class="divide-y divide-n-weak border-t border-n-weak">
            <div
              v-for="(agent, index) in agentList"
              :key="agent.email"
              class="flex justify-between flex-row items-start gap-4 py-4"
            >
              <div class="flex items-center gap-4">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.name"
                  :status="agent.availability_status"
                  :size="40"
                  hide-offline-status
                />
                <div class="flex flex-col gap-1.5 items-start">
                  <span class="block text-heading-3 text-n-slate-12 capitalize">
                    {{ agent.name }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-body-main text-n-slate-11">
                      {{ agent.email }}
                    </span>
                    <div class="w-px h-3 bg-n-strong rounded-lg" />
                    <span
                      class="block w-fit text-body-main text-n-slate-11 relative"
                      :class="{
                        'hover:text-n-slate-12 group cursor-pointer':
                          agent.custom_role_id,
                      }"
                    >
                      {{ getAgentRoleName(agent) }}

                      <div
                        class="absolute ltr:left-0 rtl:right-0 z-10 hidden w-[300px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl outline outline-1 outline-n-container shadow-lg top-14 md:top-12"
                        :class="{ 'group-hover:block': agent.custom_role_id }"
                      >
                        <div class="flex flex-col gap-1 p-4">
                          <span class="text-heading-3 text-n-slate-12">
                            {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                          </span>
                          <!-- Permissions are API-defined keys from the fixed translation catalog. -->
                          <!-- eslint-disable @intlify/vue-i18n/no-dynamic-keys -->
                          <ul class="ltr:pl-4 rtl:pr-4 mb-0 list-disc">
                            <li
                              v-for="permission in getAgentRolePermissions(
                                agent
                              )"
                              :key="permission"
                              class="text-body-main text-n-slate-11"
                            >
                              {{
                                $t(
                                  `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                                )
                              }}
                            </li>
                          </ul>
                          <!-- eslint-enable @intlify/vue-i18n/no-dynamic-keys -->
                        </div>
                      </div>
                    </span>
                    <div class="w-px h-3 bg-n-strong rounded-lg" />
                    <span
                      v-if="agent.confirmed"
                      class="text-body-main text-n-slate-11"
                    >
                      {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                    </span>
                    <span
                      v-if="!agent.confirmed"
                      class="text-body-main text-n-slate-11"
                    >
                      {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex justify-end gap-3">
                <Button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  :aria-label="`${$t('AGENT_MGMT.EDIT.BUTTON_TEXT')}: ${agent.name}`"
                  @click="openEditPopup(agent)"
                />
                <Button
                  v-if="showDeleteAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  :aria-label="`${$t('AGENT_MGMT.DELETE.BUTTON_TEXT')}: ${agent.name}`"
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent, index)"
                />
              </div>
            </div>
          </div>
          <PaginationFooter
            v-if="agentsMeta.totalEntries > agentsMeta.perPage"
            :current-page="agentsMeta.currentPage"
            :total-items="agentsMeta.totalEntries"
            :items-per-page="agentsMeta.perPage"
            class="!px-0"
            @update:current-page="changePage"
          />
        </template>

        <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
          <AddAgent @close="hideAddPopup" />
        </woot-modal>

        <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
          <EditAgent
            v-if="showEditPopup"
            :id="currentAgent.id"
            :name="currentAgent.name"
            :provider="currentAgent.provider"
            :type="currentAgent.role"
            :email="currentAgent.email"
            :availability="currentAgent.availability_status"
            :custom-role-id="currentAgent.custom_role_id"
            :custom-attributes="currentAgent.custom_attributes"
            :team-ids="currentAgent.team_ids"
            @close="hideEditPopup"
          />
        </woot-modal>

        <woot-delete-modal
          v-model:show="showDeletePopup"
          :on-close="closeDeletePopup"
          :on-confirm="confirmDeletion"
          :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
          :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
          :message-value="deleteMessage"
          :confirm-text="deleteConfirmText"
          :reject-text="deleteRejectText"
        />
      </SettingsLayout>
    </div>
    <div class="hidden lg:flex w-[320px] flex-col shrink-0 gap-4 mt-8">
      <div class="bg-n-surface-0 border border-n-weak rounded-xl p-6">
        <div class="flex items-center gap-2 mb-4">
          <div class="text-n-slate-12">
            <i class="i-lucide-braces text-xl" />
          </div>
          <h3 class="text-heading-3 text-n-slate-12 font-medium m-0">
            Integração via API
          </h3>
        </div>

        <p class="text-body-main text-n-slate-11 mb-4">
          Você pode automatizar a busca e relatórios de agentes usando nossa API
          Inteligente.
        </p>

        <div
          class="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 break-all"
        >
          <div class="mb-1 text-slate-500">// Listagem Padrão</div>
          <div class="mb-4">
            <span class="text-emerald-400">GET</span> {{ apiBaseUrl }}
          </div>

          <div class="mb-1 text-slate-500">
            // Filtrar por Documento (CPF) sem traços
          </div>
          <div class="mb-4">
            <span class="text-emerald-400">GET</span>
            {{ apiBaseUrl }}?cpf=12384478648
          </div>

          <div class="mb-1 text-slate-500">// Filtrar por Email</div>
          <div class="mb-5">
            <span class="text-emerald-400">GET</span>
            {{ apiBaseUrl }}?email=atendente@empresa.com
          </div>

          <div class="text-yellow-400 mb-1">Headers:</div>
          <div>
            api-access-token:
            <span class="text-slate-400">
              Seu Token de API (Perfil > Configurações de Perfil)
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
